import { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "../utils/axios";
import { refreshAccessToken as requestNewAccessToken } from "../utils/auth";

export const AuthContext = createContext();

// ASP.NET's JwtSecurityTokenHandler maps ClaimTypes.Role to the short "role"
// key when writing the token in most default setups, but falls back to the
// full ClaimTypes URI if outbound claim mapping was disabled server-side -
// check both so this doesn't silently break on a server-side config change.
const ROLE_CLAIM_URI = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
const getRoleFromToken = (decoded) => decoded?.role ?? decoded?.[ROLE_CLAIM_URI] ?? null;

const AuthProvider = ({ children }) => {
    const API_URL = process.env.REACT_APP_API_URL;
    const [user, setUser] = useState(null);
    const [currLocation, setCurrLocation] = useState(localStorage.getItem("locationUid") || null);
    const [locations, setLocationsState] = useState(() => {
        const storedLocations = localStorage.getItem("locations");
        return storedLocations ? JSON.parse(storedLocations) : [];
    });
    const [features, setFeatures] = useState([]);
    const [featuresLoading, setFeaturesLoading] = useState(true);
    const [sections, setSections] = useState([]);
    const [sectionsLoading, setSectionsLoading] = useState(true);

    const locationsMemo = useMemo(() => locations, [locations]);

    const role = getRoleFromToken(user);
    const isAdmin = role === "Administrator" || role === "1" || role === 1;

    const saveTokens = (accessToken, refreshToken) => {
        if (!accessToken || !refreshToken) return;

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        setUser(jwtDecode(accessToken));
    };

    const refreshAccessToken = async () => {
        try {
            const newAccessToken = await requestNewAccessToken();
            setUser(jwtDecode(newAccessToken));
            return newAccessToken;
        } catch (error) {
            // Only a rejected refresh token (401) means the session is over. Network
            // errors / cold backend starts keep the tokens so the next request can retry.
            if (!error.response || error.response.status >= 500) {
                return null;
            }
            logout();
            return null;
        }
    };

    useEffect(() => {
        const verifyAndSetUser = async () => {
            let accessToken = localStorage.getItem("accessToken");

            if (!accessToken && !localStorage.getItem("refreshToken")) {
                logout();
                return;
            }

            if (!accessToken || jwtDecode(accessToken).exp * 1000 < Date.now()) {
                accessToken = await refreshAccessToken();
            }

            if (accessToken) {
                setUser(jwtDecode(accessToken));
            }
        };

        verifyAndSetUser();
    }, []);

    useEffect(() => {
        const fetchFeatures = async () => {
            if (!currLocation) {
                setFeatures([]);
                setFeaturesLoading(false);
                return;
            }

            setFeaturesLoading(true);
            try {
                const response = await axios.get(`/features/${currLocation}`);
                setFeatures(response.data || []);
            } catch (error) {
                console.error("🚨 Failed to fetch features:", error);
                setFeatures([]);
            } finally {
                setFeaturesLoading(false);
            }
        };

        fetchFeatures();
    }, [currLocation]);

    // Sections are shared across the app (Sections page, the ticket-creation
    // modals, the recurring-ticket-creation modal) - fetching them once here
    // per location instead of independently in each consumer avoids firing
    // the same GET /section/location/{id} request repeatedly.
    const fetchSections = useCallback(async () => {
        if (!currLocation) {
            setSections([]);
            setSectionsLoading(false);
            return;
        }

        setSectionsLoading(true);
        try {
            const response = await axios.get(`/section/location/${currLocation}`);
            setSections(response.data || []);
        } catch (error) {
            console.error("🚨 Failed to fetch sections:", error);
            setSections([]);
        } finally {
            setSectionsLoading(false);
        }
    }, [currLocation]);

    useEffect(() => {
        fetchSections();
    }, [fetchSections]);

    const login = async (email, password) => {
        try {
            const res = await axios.post(`${API_URL}/auth/login`, { email, password });
            saveTokens(res.data.tokens.accessToken, res.data.tokens.refreshToken);
        } catch (error) {
            console.error("🚨 Login failed:", error.response?.data || error.message);
            throw error;
        }
    };

    const logout = () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("locationUid");
        localStorage.removeItem("locations");
        setUser(null);
        setCurrLocation(null);
        setLocationsState([]);
        setFeatures([]);
        setSections([]);
    };

    const setLocation = (location) => {
        if (currLocation !== location) {
            localStorage.setItem("locationUid", location);
            setCurrLocation(location);
        }
    };

    const setLocations = (newLocations) => {
        setLocationsState(newLocations);
        localStorage.setItem("locations", JSON.stringify(newLocations));
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                role,
                isAdmin,
                isAuthenticated: !!user,
                login,
                logout,
                currLocation,
                setLocation,
                locations: locationsMemo,
                setLocations,
                features,
                featuresLoading,
                sections,
                sectionsLoading,
                refetchSections: fetchSections,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
